import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CheatSheet from '../../components/admin/CheatSheet';
import {
  Users, GraduationCap, BarChart3, Upload, Plus, Search, Trash2, Edit2,
  Check, X, Loader2, LogOut, School, AlertTriangle, Key, Copy, RefreshCw,
  Clock, Mail, FileText, ChevronDown, Settings, Image, BookOpen, Heart, Menu,
  HelpCircle, Activity, Zap, Award, Shield, Send, ChevronUp, Bell, Trophy,
  Download, UserPlus, Calendar, Palette, TrendingUp, UserCheck, CheckCircle, XCircle,
  Eye, ArrowLeft, Brain, Building2, Target, MapPin, Globe, Briefcase, Star, Maximize2, ChevronRight
} from 'lucide-react';
import CorporateOnboardingWizard from './CorporateOnboardingWizard';
import LeaderboardTab from './CorporateLeaderboardTab';
import BoardPresentation from './BoardPresentation';
import DashboardCover from '../../components/DashboardCover';
import { StatCard, DetailRow, TimelineStep } from './SharedComponents';
import OverviewTab from './tabs/OverviewTab';
import EngagementTab from './tabs/EngagementTab';
import WellbeingTab from './tabs/WellbeingTab';
import ScenariosTab from './tabs/ScenariosTab';
import BadgesTab from './tabs/BadgesTab';
import PulseTab from './tabs/PulseTab';
import HealthMapTab from './tabs/HealthMapTab';
import GoalsTab from './tabs/GoalsTab';
import BenchmarkTab from './tabs/BenchmarkTab';
import RoiTab from './tabs/RoiTab';
import StaffTab from './tabs/StaffTab';
import StudentsTab from './tabs/StudentsTab';
import AccessCodesTab from './tabs/AccessCodesTab';
import ReportsTab from './tabs/ReportsTab';
import LocationsTab from './tabs/LocationsTab';
import RollupTab from './tabs/RollupTab';
import MessagesTab from './tabs/MessagesTab';
import SettingsTab from './tabs/SettingsTab';
import GuideTab from './tabs/GuideTab';
import ProductDocsTab from './tabs/ProductDocsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CorporateDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [org, setOrg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [usage, setUsage] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [accessCodes, setAccessCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState('');
  const [leadFilter, setLeadFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: '', last_name: '', grade: '', section: '', teacher_name: '', gender: '', parent_email: '', parent_name: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [regenerating, setRegenerating] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [reports, setReports] = useState([]);
  const [pastoral, setPastoral] = useState(null);
  const [oceanAggregates, setOceanAggregates] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const logoFileRef = useRef(null);
  const fileRef = useRef(null);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardChecked, setWizardChecked] = useState(false);
  const [hasSkippedSteps, setHasSkippedSteps] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [usageDetail, setUsageDetail] = useState(null);
  const [schoolSettings, setSchoolSettings] = useState({ nft_rewards_enabled: true, nudge_enabled: true, nudge_inactive_days: 3, max_daily_usage_minutes: 60 });
  const [guide, setGuide] = useState(null);
  const [guideOpen, setGuideOpen] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [nudging, setNudging] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgForm, setMsgForm] = useState({ subject: '', message: '', category: 'general' });
  const [sendingMsg, setSendingMsg] = useState(false);
  const [msgSent, setMsgSent] = useState(false);

  // New enhancement states
  const [alerts, setAlerts] = useState([]);
  const [alertSummary, setAlertSummary] = useState({ urgent: 0, concern: 0, watch: 0 });
  const [benchmarks, setBenchmarks] = useState(null);
  const [certificates, setCertificates] = useState(null);
  const [staff, setStaff] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [showInviteStaff, setShowInviteStaff] = useState(false);
  const [staffForm, setStaffForm] = useState({ email: '', name: '', role: 'teacher', classes: [] });
  const [invitingStaff, setInvitingStaff] = useState(false);
  const [calendarTerms, setCalendarTerms] = useState([]);
  const [inHoliday, setInHoliday] = useState(false);
  const [showAddTerm, setShowAddTerm] = useState(false);
  const [termForm, setTermForm] = useState({ name: '', start_date: '', end_date: '', type: 'term' });
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [slotForm, setSlotForm] = useState({ day: 'Monday', start_time: '09:00', end_time: '09:30', grade: '', label: 'Practice Session' });
  const [branding, setBranding] = useState({ primary_color: '#F5831F', tagline: '' });
  const [parentReportHistory, setParentReportHistory] = useState([]);
  const [sendingParentReports, setSendingParentReports] = useState(false);
  const [parentReportResult, setParentReportResult] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardOptIn, setLeaderboardOptIn] = useState(true);

  // Enterprise feature states (Batch 2 & 3)
  const [scenarios, setScenarios] = useState(null);
  const [pulseData, setPulseData] = useState(null);
  const [badgesData, setBadgesData] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [qbrData, setQbrData] = useState(null);
  const [locationsData, setLocationsData] = useState([]);
  const [subAdmins, setSubAdmins] = useState([]);
  const [caseStudy, setCaseStudy] = useState(null);
  const [deptLeaderboard, setDeptLeaderboard] = useState(null);
  const [showAddScenario, setShowAddScenario] = useState(false);
  const [scenarioForm, setScenarioForm] = useState({ title: '', description: '', category: 'General', difficulty: 'intermediate', department: '' });
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [locationForm, setLocationForm] = useState({ name: '', city: '', state: '', country: 'United States', timezone_str: 'America/New_York' });
  const [showInviteSubAdmin, setShowInviteSubAdmin] = useState(false);
  const [subAdminForm, setSubAdminForm] = useState({ email: '', name: '', location_id: '', permissions: ['view_analytics', 'manage_members'] });
  const [generatingCaseStudy, setGeneratingCaseStudy] = useState(false);
  const [generatingQbr, setGeneratingQbr] = useState(false);

  // Phase 2 enterprise states
  const [heatmapData, setHeatmapData] = useState(null);
  const [goalsData, setGoalsData] = useState(null);
  const [insightAlerts, setInsightAlerts] = useState(null);
  const [digestSettings, setDigestSettings] = useState(null);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [goalForm, setGoalForm] = useState({ title: '', metric_type: 'ocean_completion', target_value: '', deadline: '', description: '' });
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const [savingDigest, setSavingDigest] = useState(false);
  const [ccInput, setCcInput] = useState('');

  // Phase 3 enterprise states
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [showBoardPresentation, setShowBoardPresentation] = useState(false);
  const [rollupData, setRollupData] = useState(null);
  const [scopeData, setScopeData] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [scopedAnalytics, setScopedAnalytics] = useState(null);

  const getToken = () => localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

  const US_GRADES = ['6th', '7th', '8th', '9th', '10th', '11th', '12th'];

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [schoolRes, summaryRes, dashRes, usageRes, codesRes, reportsRes, pastoralRes, oceanRes, usageDetailRes, settingsRes, guideRes, leaderboardRes] = await Promise.all([
        fetch(`${API_URL}/api/corporate/info`, { headers }),
        fetch(`${API_URL}/api/corporate/roster-summary`, { headers }),
        fetch(`${API_URL}/api/corporate/dashboard`, { headers }),
        fetch(`${API_URL}/api/corporate/usage`, { headers }),
        fetch(`${API_URL}/api/corporate/access-codes`, { headers }),
        fetch(`${API_URL}/api/reports/corporate`, { headers }),
        fetch(`${API_URL}/api/corporate/pastoral`, { headers }),
        fetch(`${API_URL}/api/ocean/corporate-aggregates`, { headers }),
        fetch(`${API_URL}/api/corporate/usage-detail`, { headers }),
        fetch(`${API_URL}/api/corporate/settings`, { headers }),
        fetch(`${API_URL}/api/corporate/guide`, { headers }),
        fetch(`${API_URL}/api/corporate/leaderboard`, { headers }),
      ]);
      if (schoolRes.ok) setOrg(await schoolRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (usageRes.ok) setUsage(await usageRes.json());
      if (codesRes.ok) {
        const cData = await codesRes.json();
        setAccessCodes(cData.codes || []);
      }
      if (reportsRes.ok) {
        const rData = await reportsRes.json();
        setReports(rData.reports || []);
      }
      if (pastoralRes.ok) setPastoral(await pastoralRes.json());
      if (oceanRes.ok) setOceanAggregates(await oceanRes.json());
      if (usageDetailRes.ok) setUsageDetail(await usageDetailRes.json());
      if (settingsRes.ok) setSchoolSettings(await settingsRes.json());
      if (guideRes.ok) setGuide(await guideRes.json());
      if (leaderboardRes.ok) {
        const lb = await leaderboardRes.json();
        setLeaderboard(lb.leaderboard || []);
      }
      // Fetch messages
      try {
        const msgRes = await fetch(`${API_URL}/api/corporate/messages/my`, { headers });
        if (msgRes.ok) { const md = await msgRes.json(); setMessages(md.messages || []); }
      } catch {}
      // Fetch enhancement data
      try {
        const [alertsRes, benchRes, certsRes, staffRes, calRes, schedRes, brandRes, prHistRes, analyticsRes] = await Promise.all([
          fetch(`${API_URL}/api/corporate/wellbeing/alerts`, { headers }),
          fetch(`${API_URL}/api/corporate/benchmarks`, { headers }),
          fetch(`${API_URL}/api/corporate/certificates`, { headers }),
          fetch(`${API_URL}/api/corporate/staff`, { headers }),
          fetch(`${API_URL}/api/corporate/calendar`, { headers }),
          fetch(`${API_URL}/api/corporate/schedule`, { headers }),
          fetch(`${API_URL}/api/corporate/branding`, { headers }),
          fetch(`${API_URL}/api/corporate/reports/parent-history`, { headers }),
          fetch(`${API_URL}/api/corporate/analytics`, { headers }),
        ]);
        if (alertsRes.ok) { const ad = await alertsRes.json(); setAlerts(ad.alerts || []); setAlertSummary(ad.summary || {}); }
        if (benchRes.ok) setBenchmarks(await benchRes.json());
        if (certsRes.ok) setCertificates(await certsRes.json());
        if (staffRes.ok) { const sd = await staffRes.json(); setStaff(sd.staff || []); setAvailableClasses(sd.available_classes || []); }
        if (calRes.ok) { const cd = await calRes.json(); setCalendarTerms(cd.terms || []); setInHoliday(cd.in_holiday || false); }
        if (schedRes.ok) { const sd = await schedRes.json(); setScheduleSlots(sd.slots || []); }
        if (brandRes.ok) setBranding(await brandRes.json());
        if (prHistRes.ok) { const ph = await prHistRes.json(); setParentReportHistory(ph.reports || []); }
        if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      } catch {}
      // Fetch enterprise Batch 2 & 3 data
      try {
        const [scenariosRes, pulseRes, badgesRes, sentimentRes, qbrRes, locRes, saRes, deptLbRes] = await Promise.all([
          fetch(`${API_URL}/api/corporate/scenarios`, { headers }),
          fetch(`${API_URL}/api/corporate/pulse-surveys`, { headers }),
          fetch(`${API_URL}/api/corporate/badges`, { headers }),
          fetch(`${API_URL}/api/corporate/sentiment-trend`, { headers }),
          fetch(`${API_URL}/api/corporate/qbr-report`, { headers }),
          fetch(`${API_URL}/api/corporate/locations`, { headers }),
          fetch(`${API_URL}/api/corporate/sub-admins`, { headers }),
          fetch(`${API_URL}/api/corporate/department-leaderboard`, { headers }),
        ]);
        if (scenariosRes.ok) setScenarios(await scenariosRes.json());
        if (pulseRes.ok) setPulseData(await pulseRes.json());
        if (badgesRes.ok) setBadgesData(await badgesRes.json());
        if (sentimentRes.ok) setSentimentData(await sentimentRes.json());
        if (qbrRes.ok) setQbrData(await qbrRes.json());
        if (locRes.ok) { const ld = await locRes.json(); setLocationsData(ld.locations || []); }
        if (saRes.ok) { const sd = await saRes.json(); setSubAdmins(sd.sub_admins || []); }
        if (deptLbRes.ok) setDeptLeaderboard(await deptLbRes.json());
      } catch {}
      // Fetch Phase 2 enterprise data
      try {
        const [hmRes, goalsRes, alertsRes, digestRes] = await Promise.all([
          fetch(`${API_URL}/api/corporate/health-heatmap`, { headers }),
          fetch(`${API_URL}/api/corporate/goals`, { headers }),
          fetch(`${API_URL}/api/corporate/insight-alerts`, { headers }),
          fetch(`${API_URL}/api/corporate/email-digest/settings`, { headers }),
        ]);
        if (hmRes.ok) setHeatmapData(await hmRes.json());
        if (goalsRes.ok) setGoalsData(await goalsRes.json());
        if (alertsRes.ok) setInsightAlerts(await alertsRes.json());
        if (digestRes.ok) setDigestSettings(await digestRes.json());
      } catch {}
      // Fetch Phase 3 data (benchmarking, ROI, forecast)
      try {
        const [bmRes, roiRes, fcRes] = await Promise.all([
          fetch(`${API_URL}/api/corporate/competitor-benchmark`, { headers }),
          fetch(`${API_URL}/api/corporate/roi-calculator`, { headers }),
          fetch(`${API_URL}/api/corporate/engagement-forecast`, { headers }),
        ]);
        if (bmRes.ok) setBenchmarkData(await bmRes.json());
        if (roiRes.ok) setRoiData(await roiRes.json());
        if (fcRes.ok) setForecastData(await fcRes.json());
      } catch {}
      // Fetch location rollup & scope
      try {
        const [rollupRes, scopeRes] = await Promise.all([
          fetch(`${API_URL}/api/corporate/location-rollup`, { headers }),
          fetch(`${API_URL}/api/corporate/sub-admin/my-scope`, { headers }),
        ]);
        if (rollupRes.ok) setRollupData(await rollupRes.json());
        if (scopeRes.ok) setScopeData(await scopeRes.json());
      } catch {}
      // Fetch leaderboard lazily (not in initial load)
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchStudents = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    const params = new URLSearchParams();
    if (teamFilter) params.append('grade', teamFilter);
    if (leadFilter) params.append('teacher', leadFilter);
    if (searchQuery) params.append('search', searchQuery);
    try {
      const res = await fetch(`${API_URL}/api/corporate/members?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.students || []);
        setMemberTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
  }, [teamFilter, leadFilter, searchQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (org?.status === 'active') fetchStudents(); }, [fetchStudents, org?.status]);

  // Check wizard status on first load
  useEffect(() => {
    if (wizardChecked || !org || org.status !== 'active') return;
    setWizardChecked(true);
    const token = getToken();
    if (!token) return;
    fetch(`${API_URL}/api/corporate/wizard/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          if (data.show_wizard) setShowWizard(true);
          if (data.has_skipped_steps) setHasSkippedSteps(true);
        }
      })
      .catch(() => {});
  }, [org, wizardChecked]);

  const addStudent = async () => {
    if (!addForm.first_name.trim() || !addForm.last_name.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/corporate/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(addForm),
      });
      if (res.ok) {
        setAddForm({ first_name: '', last_name: '', grade: '', section: '', teacher_name: '', gender: '', parent_email: '', parent_name: '' });
        setShowAddForm(false);
        fetchStudents();
        fetchData();
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/corporate/members/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      setUploadResult(data);
      if (data.added > 0) { fetchStudents(); fetchData(); }
    } catch (e) { console.error(e); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteStudent = async (studentId, name) => {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    try {
      await fetch(`${API_URL}/api/corporate/members/${studentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      fetchStudents();
      fetchData();
    } catch (e) { console.error(e); }
  };

  const startEdit = (s) => {
    setEditingStudent(s.student_id);
    setEditForm({ first_name: s.first_name, last_name: s.last_name, grade: s.grade, section: s.section, teacher_name: s.teacher_name, gender: s.gender, parent_email: s.parent_email || '', parent_name: s.parent_name || '' });
  };

  const saveEdit = async (studentId) => {
    try {
      await fetch(`${API_URL}/api/corporate/members/${studentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(editForm),
      });
      setEditingStudent(null);
      fetchStudents();
    } catch (e) { console.error(e); }
  };

  const regenerateCode = async () => {
    setRegenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/corporate/access-codes/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
    setRegenerating(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F7F4' }}>
      <Loader2 className="animate-spin" size={32} style={{ color: '#F5831F' }} />
    </div>
  );

  /* ─── PENDING APPROVAL SCREEN ──────────────────────────────────────────── */
  if (org && org.status === 'pending_approval') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="max-w-lg w-full" data-testid="pending-approval-screen">
          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #E8E8E8' }}>
            {/* Header bar */}
            <div className="px-8 py-5" style={{ backgroundColor: '#1A1209' }}>
              <div className="flex items-center gap-2">
                <School size={20} style={{ color: '#F5831F' }} />
                <span className="text-sm font-bold text-white">HiBFF Corporate</span>
              </div>
            </div>

            <div className="p-4 sm:p-8">
              {/* Status badge */}
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: '#F5831F' }} />
                <span className="text-sm font-semibold" style={{ color: '#F5831F' }} data-testid="status-badge">Under Review</span>
              </div>

              <h1 className="text-xl font-bold mb-2" style={{ color: '#1A1209' }}>
                Welcome, {org.contact_name}
              </h1>
              <p className="text-sm mb-6" style={{ color: '#888' }}>
                Your application for <strong style={{ color: '#1A1209' }}>{org.org_name}</strong> has been received and is being reviewed by our team.
              </p>

              {/* Application details */}
              <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#F8F7F4' }}>
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: '#999' }}>Application Details</h3>
                <div className="space-y-3">
                  <DetailRow icon={<FileText size={14} />} label="Organisation" value={org.org_name} />
                  <DetailRow icon={<Clock size={14} />} label="Submitted" value={new Date(org.submitted_at || org.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} />
                  <DetailRow icon={<AlertTriangle size={14} />} label="Status" value="Under Review" valueColor="#F5831F" />
                  <DetailRow icon={<Mail size={14} />} label="Contact Email" value={org.admin_email} />
                </div>
              </div>

              {/* Timeline */}
              <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#1E40AF' }}>What happens next</h3>
                <ol className="space-y-2.5">
                  <TimelineStep num="1" text="Our team reviews your application (within 48 hours)" active />
                  <TimelineStep num="2" text="You receive an activation email with login details and next steps" />
                  <TimelineStep num="3" text="Upload your student roster or share a parent enrollment code" />
                  <TimelineStep num="4" text="Parents activate student accounts — students begin building social confidence" />
                </ol>
              </div>

              <p className="text-xs text-center" style={{ color: '#BBB' }}>
                Questions? Reach us at <a href="mailto:support@hibff.com" className="underline" style={{ color: '#7B4FD8' }}>support@hibff.com</a>
              </p>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 flex justify-between items-center" style={{ borderTop: '1px solid #F0F0F0' }}>
              <span className="text-xs" style={{ color: '#CCC' }}>Logged in as {user?.email}</span>
              <button onClick={logout} className="flex items-center gap-1.5 text-xs font-medium" style={{ color: '#999' }} data-testid="pending-logout-btn">
                <LogOut size={12} /> Log Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ─── ACTIVE DASHBOARD ─────────────────────────────────────────────────── */
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'leaderboard', label: 'Leaderboard', icon: TrendingUp },
    { id: 'engagement', label: 'Engagement', icon: Activity },
    { id: 'pastoral', label: 'Wellbeing', icon: Heart },
    { id: 'scenarios', label: 'Scenarios', icon: BookOpen },
    { id: 'badges', label: 'Badges', icon: Award },
    { id: 'pulse', label: 'Pulse Survey', icon: Zap },
    { id: 'health-map', label: 'Health Map', icon: Activity },
    { id: 'goals', label: 'Goals & OKRs', icon: Target },
    { id: 'benchmark', label: 'Benchmarking', icon: Globe },
    { id: 'roi', label: 'ROI Calculator', icon: TrendingUp },
    { id: 'students', label: 'Team Roster', icon: Users },
    { id: 'staff', label: 'Team Leads', icon: UserCheck },
    { id: 'access-codes', label: 'Access Codes', icon: Key },
    { id: 'reports', label: 'Reports & QBR', icon: FileText },
    { id: 'locations', label: 'Locations', icon: Building2 },
    { id: 'rollup', label: 'Location Roll-Up', icon: Globe },
    { id: 'messages', label: 'Contact HiBFF', icon: Mail },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'guide', label: 'How-To Guide', icon: HelpCircle },
    { id: 'product-docs', label: 'Product Docs', icon: FileText },
  ];

  const activeCode = accessCodes.find(c => c.active);
  const maxUsage = usage?.weekly_usage ? Math.max(...usage.weekly_usage.map(w => w.sessions), 1) : 1;

  const ctx = {
    org, setOrg, user, summary, dashboard, usage, members, memberTotal,
    accessCodes, leaderboard, reports, certificates, staff,
    availableClasses, calendarTerms, inHoliday, scheduleSlots,
    branding, parentReportHistory, analytics, scenarios, pulseData,
    badgesData, sentimentData, qbrData, locationsData, subAdmins,
    deptLeaderboard, heatmapData, goalsData, insightAlerts,
    digestSettings, benchmarkData, roiData, forecastData, rollupData,
    scopeData, usageDetail, pastoral, oceanAggregates, alerts,
    alertSummary, schoolSettings, caseStudy, nudgeResult,
    maxUsage, API_URL, getToken, navigate, fetchData, fetchStudents,
    nudging, generatingCaseStudy, showAddForm, showBulkUpload,
    showAddScenario, showAddGoal, showInviteStaff, showAddLocation,
    showInviteSubAdmin, invitingStaff, saving, uploading, uploadResult,
    previewing, regenerating, codeCopied, logoPreviewUrl, logoUploading,
    logoFile, showAddTerm, showAddSlot,
    addForm, editForm, editingStudent, staffForm, scenarioForm,
    goalForm, termForm, slotForm, locationForm, subAdminForm,
    guideOpen, guide, benchmarks,
    setTab, setNudging, setNudgeResult, setGeneratingCaseStudy,
    setCaseStudy, setShowBoardPresentation, setShowAddForm,
    setShowBulkUpload, setShowAddScenario, setShowAddGoal,
    setShowInviteStaff, setShowAddLocation, setShowInviteSubAdmin,
    setAddForm, setEditForm, setEditingStudent, setStaffForm,
    setScenarioForm, setGoalForm, setTermForm, setSlotForm,
    setLocationForm, setSubAdminForm, setGuideOpen,
    setScenarios, setGoalsData, setAlerts, setAlertSummary,
    setLocationsData, setSubAdmins,
    setSchoolSettings, setBranding, setCalendarTerms, setScheduleSlots,
    setLogoPreviewUrl, setLogoUploading, setLogoFile, setShowAddTerm,
    setShowAddSlot, setSaving, setUploading, setUploadResult,
    setPreviewing, setRegenerating, setCodeCopied, setInvitingStaff,
    setDigestSettings,
    fileRef, logoFileRef,
    searchQuery, setSearchQuery, teamFilter, setTeamFilter, leadFilter, setLeadFilter,
    msgForm, setMsgForm, sendingMsg, setSendingMsg, msgSent, setMsgSent,
    messages, setMessages,
    sendingParentReports, setSendingParentReports, parentReportResult, setParentReportResult,
    selectedStudents, setSelectedStudents, bulkAction, setBulkAction,
    leaderboardData, setLeaderboardData, leaderboardOptIn, setLeaderboardOptIn,
    generatingQbr, setGeneratingQbr, setQbrData,
    sendingDigest, setSendingDigest, digestResult, setDigestResult,
    savingDigest, setSavingDigest, ccInput, setCcInput,
    selectedLocationId, setSelectedLocationId, scopedAnalytics, setScopedAnalytics,
    US_GRADES,
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F8F7F4' }}>
      {/* Dashboard Cover */}
      <DashboardCover
        orgName={org?.org_name}
        logoUrl={org?.logo_url}
        subtitle="Corporate Dashboard"
        accentColor="#7B4FD8"
        onSettingsClick={() => { setTab('settings'); }}
      />

      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static top-0 left-0 h-full md:h-auto w-56 flex flex-col z-40 transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} style={{ backgroundColor: '#5A4A62' }}>
        {/* Mobile close */}
        <button onClick={() => setMobileSidebarOpen(false)} className="md:hidden absolute top-4 right-3 text-white/60 hover:text-white z-50" aria-label="Close">
          <X size={22} />
        </button>

        <div className="p-5 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <School size={20} style={{ color: '#E8B4F8' }} />
            <span className="text-sm font-bold text-white">HiBFF Corporate</span>
          </div>
          {org && <p className="text-xs truncate" style={{ color: '#CBBFD2' }}>{org.org_name}</p>}
        </div>
        <nav className="flex-1 px-3">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => { setTab(t.id); if (window.innerWidth < 768) setMobileSidebarOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-sm transition-all"
                style={active ? { backgroundColor: '#F5831F22', color: '#F5831F' } : { color: '#CBBFD2' }}
                data-testid={`tab-${t.id}`}>
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 mt-auto" style={{ borderTop: '1px solid #6A5A72' }}>
          {/* Setup Guide button */}
          <button
            onClick={() => setShowWizard(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 mb-2 rounded-lg text-sm transition-all"
            style={{ color: '#CBBFD2' }}
            data-testid="setup-guide-btn"
          >
            <div className="relative">
              <BookOpen size={16} />
              {hasSkippedSteps && (
                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ backgroundColor: '#F5831F' }} data-testid="setup-guide-dot" />
              )}
            </div>
            Setup Guide
          </button>
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: '#F5831F' }}>
              {user?.name?.[0] || 'S'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">{user?.name}</p>
              <p className="text-[10px]" style={{ color: '#CBBFD2' }}>Corporate Admin</p>
            </div>
            <button onClick={logout} className="p-1 rounded" title="Logout" data-testid="logout-btn">
              <LogOut size={14} style={{ color: '#CBBFD2' }} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Mobile header */}
        <div className="flex items-center gap-3 mb-4 md:hidden">
          <button onClick={() => setMobileSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Open menu" data-testid="corporate-mobile-menu">
            <Menu size={22} style={{ color: '#1A1209' }} />
          </button>
          <div className="flex items-center gap-2">
            <School size={18} style={{ color: '#F5831F' }} />
            <span className="text-sm font-bold" style={{ color: '#1A1209' }}>HiBFF Corporate</span>
          </div>
        </div>
        {/* ── Tab Content ───────────────────────────────────────── */}
        <CheatSheet pageId="corporate-dashboard" />
        {tab === 'overview' && <OverviewTab ctx={ctx} />}
        {tab === 'leaderboard' && (
          <LeaderboardTab
            leaderboardData={leaderboard}
            benchmarks={benchmarks}
            API_URL={API_URL}
            getToken={getToken}
          />
        )}
        {tab === 'engagement' && <EngagementTab ctx={ctx} />}
        {tab === 'pastoral' && <WellbeingTab ctx={ctx} />}
        {tab === 'scenarios' && <ScenariosTab ctx={ctx} />}
        {tab === 'badges' && <BadgesTab ctx={ctx} />}
        {tab === 'pulse' && <PulseTab ctx={ctx} />}
        {tab === 'health-map' && <HealthMapTab ctx={ctx} />}
        {tab === 'goals' && <GoalsTab ctx={ctx} />}
        {tab === 'benchmark' && <BenchmarkTab ctx={ctx} />}
        {tab === 'roi' && <RoiTab ctx={ctx} />}
        {tab === 'staff' && <StaffTab ctx={ctx} />}
        {tab === 'students' && <StudentsTab ctx={ctx} />}
        {tab === 'access-codes' && <AccessCodesTab ctx={ctx} />}
        {tab === 'reports' && <ReportsTab ctx={ctx} />}
        {tab === 'locations' && <LocationsTab ctx={ctx} />}
        {tab === 'rollup' && <RollupTab ctx={ctx} />}
        {tab === 'messages' && <MessagesTab ctx={ctx} />}
        {tab === 'settings' && <SettingsTab ctx={ctx} />}
        {tab === 'guide' && <GuideTab ctx={ctx} />}
        {tab === 'product-docs' && <ProductDocsTab ctx={ctx} />}
      </main>

      {/* Onboarding Wizard */}
      {showWizard && (
        <CorporateOnboardingWizard
          school={org}
          accessCodes={accessCodes}
          getToken={getToken}
          onComplete={(skipped) => {
            setShowWizard(false);
            setHasSkippedSteps(skipped.length > 0);
            fetchData();
            fetchStudents();
          }}
          onSchoolUpdate={setOrg}
          onStudentsUpdate={() => { fetchStudents(); fetchData(); }}
        />
      )}

      {/* Board Presentation Mode */}
      {showBoardPresentation && (
        <BoardPresentation
          onClose={() => setShowBoardPresentation(false)}
          roiData={roiData}
          benchmarkData={benchmarkData}
          qbrData={qbrData}
          sentimentData={sentimentData}
          goalsData={goalsData}
          forecastData={forecastData}
          pulseData={pulseData}
          orgName={org?.org_name}
          deptLeaderboard={deptLeaderboard}
          apiUrl={API_URL}
          getToken={getToken}
        />
      )}
    </div>
  );
};

export default CorporateDashboard;
